import React from "react";
import { withRouter } from "react-router-dom";
import { flowRight as compose } from 'lodash';
import { withStore } from "../../store";
import SearchInput from "../../components/SearchInput/SearchInput";
import Loading from "../../components/Loading/Loading";
import { pluralize, fetchHandler, getClientApiURL } from "../../utils";
import { SVG } from "../../assets/images";
import "./SearchFilters.scss";
import {BREAKPOINT_TABLET_PORTRAIT} from "../../utils/constants";

const filterMenuOptions = [
  { label: "Type", value: "types"},
  { label: "Opera", value: "operas"},
  { label: "Composer", value: "composer"},
  { label: "Artist", value: "artist"},
];

const mediaTypeDropDownMenuOptions = [
  { label: "All Types", value: ""},
  { label: "HD Video", value: "hd"},
  { label: "SD Video", value: "sd"},
  { label: "Audio Only", value: "audio"},
];

const filterNameMapping = {
  "operas" : "opera",
  "types" : "media_type",
  "composer" : "composer",
  "artist" : "artist"
}

class SearchFilters extends React.Component<any, any> {
  filtersContainerRef: React.RefObject<HTMLDivElement> = React.createRef();
  triggerBeforeOpen: HTMLElement | null = null;
  // MET2-2854: the filter trigger focused immediately before a dropdown opened,
  // so Escape can return focus to it when the dropdown closes.
  lastFilterTrigger: HTMLElement | null = null;

  constructor(props) {
    super(props);
    this.state = {
      selectedFilter: undefined,
      query: undefined,
      filters: {},
      searchFilters: undefined,
      showCloseIconOnFocus: false,
      mobileFiltersVisible: false,
      searchLoading: false,
      selectedLetters: {},
    }
  }

  componentDidMount() {
    window.addEventListener("resize", this.onResize);
    // MET2-2894: trap keyboard focus inside the mobile filter overlay.
    // Without this, Tab past the last focusable in the filter list escaped
    // into the catalog tiles rendered behind the overlay; Shift+Tab past
    // the back/close header escaped into the page header. Users couldn't
    // tell what they were focusing because those elements weren't visible.
    document.addEventListener("focusin", this.onFocusIn);
  }

  componentDidUpdate(_prevProps, prevState) {
    // MET2-2894 followup: restore focus to the element that triggered the
    // overlay (the "Filters +" CTA) when the overlay closes — otherwise
    // focus is lost to <body> and a keyboard user has no clear place to
    // resume from.
    if (!prevState.mobileFiltersVisible && this.state.mobileFiltersVisible) {
      this.triggerBeforeOpen = document.activeElement as HTMLElement | null;
    }
    if (prevState.mobileFiltersVisible && !this.state.mobileFiltersVisible) {
      if (this.triggerBeforeOpen && typeof this.triggerBeforeOpen.focus === "function") {
        this.triggerBeforeOpen.focus();
      }
      this.triggerBeforeOpen = null;
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("focusin", this.onFocusIn);
  }

  onFocusIn = (e: FocusEvent) => {
    if (!this.state.mobileFiltersVisible) return;
    const container = this.filtersContainerRef.current;
    const target = e.target as HTMLElement | null;
    if (!container || !target) return;
    if (container.contains(target)) return;
    const all = Array.from(container.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
    // Filter to elements that are actually tabbable. The Back button is
    // rendered unconditionally but CSS-hidden via `visibility: hidden`
    // when no sub-filter is selected — and .focus() silently no-ops on
    // visibility:hidden elements. Checking computed style + box size
    // catches both that case and display:none ancestors.
    const focusables = all.filter((el) => {
      const cs = window.getComputedStyle(el);
      if (cs.visibility === "hidden" || cs.display === "none") return false;
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    });
    if (focusables.length === 0) return;
    const cmp = target.compareDocumentPosition(container);
    const redirect = (cmp & Node.DOCUMENT_POSITION_PRECEDING)
      ? focusables[0]
      : focusables[focusables.length - 1];
    redirect.focus();
  };

  getFilterUrl = (filters) => {
    let url = `${getClientApiURL("/filter?")}`;
    if (filters) {
      for (const key in filters) {
        if (filters[key])
          url = url.concat(`&${key}=${encodeURIComponent(filters[key])}`);
      }
    }
    return url;
  }

  fetchFiltersData = (filters)  => {
    const url = this.getFilterUrl(filters);
    this.setState({
      searchLoading: true,
    })
    fetchHandler(url, {}, (response) => {
      this.setState({
        searchResponse: response,
        searchLoading: false,
      });
    });
  }

  onSearch = (name) => {
    const {selectedFilter, filters} = this.state;
    const selectedFilterName = filterNameMapping[selectedFilter];

    if (name) {
      filters[selectedFilterName] = name;
    } else {
      delete filters[selectedFilterName];
    }

    this.setState({
      filters,
      query: name
    });

    this.props.onSearch(filters, selectedFilterName);
  }

  onChange = (name) => {
    const {selectedFilter, searchResponse} = this.state;
    const selectedFilterName = filterNameMapping[selectedFilter];
    const filters = {};

    this.setState({
      query: name,
      searchResponse: !name || (searchResponse && searchResponse.results !== undefined && !searchResponse.results.length) ? undefined : searchResponse
    });

    if (name !== "") {
      filters[selectedFilterName] = name;
      this.fetchFiltersData(filters);
    }
  }

  setSelectedFilter = (filterType) => {
    const { filters } = this.state;
    const selectedFilterName = filterNameMapping[filterType];
    const willOpen = this.state.selectedFilter !== filterType;

    if (willOpen && Object.keys(filters).length > 0)
      this.props.onSearch(filters, selectedFilterName, false, true);

    // MET2-2854: remember the trigger so Escape can return focus to it.
    this.lastFilterTrigger = document.activeElement as HTMLElement | null;

    this.setState(
      {
        selectedFilter : willOpen ? filterType : undefined,
      },
      () => {
        // MET2-2854: on OPEN only, move focus into the just-opened dropdown
        // panel so Tab order lands inside it instead of skipping to the next
        // filter trigger (the panel renders in a sibling block after all four
        // triggers in DOM order). The willOpen guard prevents focus-theft
        // when the same trigger is clicked to toggle the panel closed.
        if (!willOpen) return;
        const panel = document.getElementById(`filter-results-${filterType}`);
        const first = panel && panel.querySelector<HTMLElement>(
          'input:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
        );
        if (first) first.focus();
      }
    );

    this.clearSearch();
    this.toggleDropDownMenu(filterType);
  }

  showSelectedFilterResults = (letter) => {
    const { selectedFilter, selectedLetters } = this.state;
    this.setState({
      selectedLetters: {
        ...selectedLetters,
        [selectedFilter]: letter.toLowerCase(),
      },
    });
  }

  scrollToLetter = (letter) => {
    const filtersContainer = document.querySelector(".filters-container"),
      filterResultsList = filtersContainer.querySelector(".filter-results-list"),
      scrollItem = filterResultsList.querySelector(`#filterResultLetter-${letter.toLowerCase()}`);

    if (scrollItem) {
      const elemRect = filterResultsList.getBoundingClientRect(),
      containerRect = filtersContainer.getBoundingClientRect();
      filterResultsList.scrollTo({top: scrollItem.offsetTop - (elemRect.top - containerRect.top)});
    }
  }

  toggleDropDownMenu = (id) => {
    // @ts-ignore
    document.querySelectorAll(".filter-results-dropdown-menu").forEach((el) => {
      if (el.getAttribute("id") === id) {
        el.classList.toggle("hide");
        // @ts-ignore
        document.querySelector(`.filter-category-${id}`).classList.toggle("expanded");
      } else {
        el.classList.remove("hide");
      }
    });
  }

  onFocus = () => {
    this.setState({showCloseIconOnFocus: true});
  }

  clearSearch = () => {
    this.setState({
      query : '',
      showCloseIconOnFocus: false,
      searchResponse: undefined,
    });
  }

  clearFilters = () => {
    const filters = {};
    this.setState({
      filters
    })
    this.props.onSearch(filters);
  }

  getSelectedFilterValue = (label) => {
    const { filters } = this.state;
    const selectedLabel = label.toLowerCase();
    if (selectedLabel === "type") {
      const option = _.find(mediaTypeDropDownMenuOptions, function(option) { return option.value === filters["media_type"] });
      return option && option.label;
    } else {
      return filters[selectedLabel];
    }
  }

  isSelected = (value) => {
    const { selectedFilter, filters } = this.state;
    const selectedFilterName = filterNameMapping[selectedFilter];
    if (filters[selectedFilterName] === value) {
      return true;
    }
  }

  getFilterResultClassName = (name) => {
    const { selectedFilter, selectedLetters, filters } = this.state;
    const currentCatalogResults = this.props.filters[selectedFilter][selectedLetters[selectedFilter]];
    const selectedFilterName = filterNameMapping[selectedFilter];
    const selectedFilterValue = filters[selectedFilterName];
    let className;

    if (currentCatalogResults.indexOf(name) < 0) {
      className = "disabled";
    } else if (selectedFilterValue === name) {
      className = "selected";
    }
    return className;
  }

  onMobileCTAClick = () => {
    this.setState({
      mobileFiltersVisible: true,
    });
    document.querySelector("body").classList.add("modal-open");
  }

  onMobileBackClick = () => {
    this.setState({
      selectedFilter: undefined,
    });
  }

  onMobileCloseClick = () => {
    if (this.state.selectedFilter) {
      this.setState({
        selectedFilter: undefined,
      });
    }
    this.setState({
      mobileFiltersVisible: false,
    });
    document.querySelector("body").classList.remove("modal-open");
  }

  onResize = () => {
    if (window.innerWidth > BREAKPOINT_TABLET_PORTRAIT) {
      this.onMobileCloseClick();
    }
  }
  getSearchResultClassName = (name) => {
    const { selectedFilter, filters } = this.state;
    const selectedFilterResults = Object.values(this.props.filters[selectedFilter]);
    const currentCatalogResults = _.uniq(_.flatten(selectedFilterResults));
    const selectedFilterName = filterNameMapping[selectedFilter];
    const selectedFilterValue = filters[selectedFilterName];
    let className;

    if (selectedFilterValue === name) {
      className = "selected";
    } else if (currentCatalogResults.indexOf(name) < 0) {
      className = "disabled";
    }

    return className;
  }

  getMediaTypeClassName = (name) => {
    const { filters, selectedFilter } = this.state;
    const selectedFilterName = filterNameMapping["types"];
    const selectedFilterValue = filters[selectedFilterName];
    const currentCatalogResults = this.props.filters[selectedFilter];
    let className;

    if ((!selectedFilterValue && name === "") || selectedFilterValue === name) {
      className = "selected";
    } else if (name !== "" && currentCatalogResults.indexOf(name.toUpperCase()) < 0) {
      className = "disabled";
    }
    return className;
  }

  render() {
    const { selectedFilter, selectedLetters, query, showCloseIconOnFocus, mobileFiltersVisible, searchResponse, searchLoading } = this.state;
    const { filters, catalog, categoryLoading } = this.props;
    const alpha = Array.from(Array(26)).map((e, i) => i + 65);
    const initialFilters = catalog && catalog.filters;
    const searchFilters = searchResponse && searchResponse.filters && selectedFilter && searchResponse.filters[selectedFilter];
    const searchResults = searchResponse && searchResponse.results;
    const selectedFilterLetter = selectedLetters[selectedFilter];

    return (
      <div className="SearchFilters">
        <button className="filters-mobile-cta btn btn-transparent black" onClick={this.onMobileCTAClick}><span>Filters +</span></button>
        <div ref={this.filtersContainerRef} className={`filters-container ${mobileFiltersVisible ? "mobile-filters-visible" : ""}`}>
          { mobileFiltersVisible &&
            <div className="filters-container-title">
              <button type="button" className={`filters-container-title-back ${selectedFilter && "active"}`} onClick={this.onMobileBackClick}><SVG.LeftPointer className="filters-container-title-back-icon" />Back</button>
              <span className="filters-container-title-label">{selectedFilter ? pluralize(filterMenuOptions.find((el) => el.value === this.state.selectedFilter).label) : "Filters"}</span>
              <button type="button" className="filters-container-title-close" onClick={this.onMobileCloseClick}>Close<SVG.Close className="filters-container-title-close-icon" /></button>
            </div>
          }
          {
            (!mobileFiltersVisible || !selectedFilter) &&
            <ul className="filter-categories">
              { filterMenuOptions.map((option, index) => {
                  const selectedFilterValue = this.getSelectedFilterValue(option.label);
                  const isExpanded = selectedFilter === option.value;
                  return (
                    <li key={index} className={`filter-category-${option.value}${selectedFilter ? selectedFilter !== option.value ? " inactive" : " active" : ""} expanded`}>
                      <button
                        type="button"
                        className="filter-category-btn"
                        onClick={() => this.setSelectedFilter(option.value)}
                        aria-expanded={isExpanded}
                        aria-controls={`filter-results-${option.value}`}
                      >
                        <span>{option.label}</span>
                        <span className={selectedFilterValue ? "has-selected-filter" : ""}>{selectedFilterValue || `All ${pluralize(option.label)}`}</span>
                      </button>
                    </li>
                  )
                })
              }
              { (((query || Object.keys(this.state.filters).length) && !showCloseIconOnFocus) || (mobileFiltersVisible && !selectedFilter)) &&
                <li className={`clear-filters ${query || Object.keys(this.state.filters).length ? "active" : ""}`}>
                  <button type="button" className="clear-filters-btn" onClick={this.clearFilters}>Clear Filters</button>
                </li>
              }
            </ul>
          }
          { selectedFilter &&
          <div
            className="filter-results"
            onKeyDown={(e) => {
              // MET2-2854: Escape closes the open dropdown and returns focus to
              // the trigger that opened it. Gated to the desktop dropdown so it
              // doesn't conflict with the mobile drawer's back/close handling.
              if (e.key === "Escape" && this.state.selectedFilter && !this.state.mobileFiltersVisible) {
                e.preventDefault();
                const trigger = this.lastFilterTrigger;
                this.setSelectedFilter(this.state.selectedFilter); // toggles closed
                if (trigger && typeof trigger.focus === "function") trigger.focus();
              }
            }}
          >
            {categoryLoading && <Loading inline={true}/>}
            {!categoryLoading &&
            <React.Fragment>
              {selectedFilter === "types" &&
                <ul className="filter-results-dropdown-menu" id={`filter-results-types`} aria-label="Filter by type">
                  {
                    mediaTypeDropDownMenuOptions.map((option, index) => {
                      const isSelected = this.getMediaTypeClassName(option.value) === "selected";
                      return (
                        <li key={index} className={this.getMediaTypeClassName(option.value)}>
                          <button type="button" onClick={() => this.onSearch(option.value)} aria-pressed={isSelected}>{option.label}</button>
                        </li>
                      )
                    })
                  }
                </ul>
              }
              {/* MET2-2884: the .search-results class is gated on
                  (showCloseIconOnFocus && query), not just focus. Otherwise
                  the A-Z alpha-list is hidden the moment the user Tabs into
                  the search input — so Tab past the input skips over the
                  letters entirely. With the gate, focus-without-query keeps
                  alpha-list visible and keyboard-reachable; typing still
                  hides the letters and shows search results. */}
              {selectedFilter !== "types" &&
                <div className={`filter-results-dropdown-menu ${(showCloseIconOnFocus && query) ? "search-results" : ""}`} id={`filter-results-${selectedFilter}`}>
                  <SearchInput key={selectedFilter} onFocus={this.onFocus}
                    placeholder={`Search ${selectedFilter !== "operas" ? pluralize(selectedFilter) : selectedFilter}`}
                    onChange={this.onChange} onClearSearch={this.clearSearch} showCloseIconOnFocus={showCloseIconOnFocus}
                    error={searchResults && searchResults.length === 0} errorText="No results"
                  />
                  {searchLoading && <Loading inline={true} />}
                  {showCloseIconOnFocus && searchResults && query &&
                    <ul className="filter-results-list search-results-list" aria-label={`Search results for ${selectedFilter}`}>
                      {
                        Object.keys(searchFilters).map(function(key) {
                          const isInAlpha = alpha.indexOf(key.toUpperCase().charCodeAt(0)) !== -1;
                          return searchFilters[key].map(function(name, index) {
                            const isSelected = this.isSelected(name);
                            return (
                              <li id={isInAlpha && index === 0 ? `filterResultLetter-${key}` : ""} key={index} className={this.getSearchResultClassName(name)}>
                                <button type="button" onClick={() => this.onSearch(name)} aria-pressed={!!isSelected}>{name}</button>
                                {isSelected && <button type="button" aria-label={`Remove ${name} filter`}><SVG.Close className="close-icon" onClick={() => this.onSearch()} /></button>}
                              </li>
                            )
                          }.bind(this))
                        }.bind(this))
                      }
                    </ul>
                  }
                  <ul className="alpha-list" aria-label="Filter by letter">
                    <li className="selected">
                      <button
                        type="button"
                        onClick={() => {
                          // MET2-2895: clear both the type filter AND the
                          // letter so the mobile list shows everything again.
                          this.onSearch();
                          this.setState({
                            selectedLetters: {
                              ...this.state.selectedLetters,
                              [selectedFilter]: undefined,
                            },
                          });
                        }}
                        aria-pressed={!selectedFilterLetter}
                      >
                        All
                      </button>
                    </li>
                    {
                      alpha.map((str, index) => {
                        const letter = String.fromCharCode(str);
                        const selectedFilterItems  = mobileFiltersVisible && searchFilters ? searchFilters[letter.toLowerCase()] : filters[selectedFilter][letter.toLowerCase()];
                        const isDisabled = !selectedFilterItems || !selectedFilterItems.length;
                        const isActive = selectedFilterLetter === letter.toLowerCase();
                        return (
                          <li key={index} className={isDisabled ? 'disabled' : isActive ? "active" : ""}>
                            <button
                              type="button"
                              disabled={isDisabled}
                              aria-pressed={isActive}
                              onClick={() => this.showSelectedFilterResults(letter)}
                            >
                              {letter}
                            </button>
                          </li>
                        )
                      })
                    }
                  </ul>
                  {/* MET2-2884 follow-up: gate the alpha-filtered results on
                      !query rather than !showCloseIconOnFocus. The earlier
                      condition meant once the user tabbed through the search
                      input (setting showCloseIconOnFocus=true), pressing an
                      A-Z letter would update state but the list wouldn't
                      render — focus-without-query was treated identically
                      to "user is actively searching". Switching to !query
                      lets keyboard users Tab past the input, press a letter,
                      and see the filtered list immediately. Typing in the
                      input still hides this list (search-results list takes
                      over via line 357 above). */}
                  {!mobileFiltersVisible && filters[selectedFilter][selectedFilterLetter] && !query &&
                    <ul className="filter-results-list" aria-label={`${selectedFilter} options`}>
                      {
                        initialFilters[selectedFilter][selectedFilterLetter].map(function(name, index){
                          const isSelected = this.isSelected(name);
                          return (
                            <li className={this.getFilterResultClassName(name)} key={index}>
                              <button type="button" onClick={() => this.onSearch(name)} aria-pressed={!!isSelected}>{name}</button>
                              {isSelected && <button type="button" aria-label={`Remove ${name} filter`}><SVG.Close className="close-icon" onClick={() => this.onSearch()} /></button>}
                            </li>
                          )
                        }.bind(this))
                        }
                      </ul>
                    }
                    {mobileFiltersVisible && filters[selectedFilter] &&
                      <ul className="filter-results-list" aria-label={`${selectedFilter} options`}>
                      {
                        // MET2-2895: when a letter is selected on mobile,
                        // narrow the list to just that letter's keys.
                        // Without this, mobile rendered every letter's items
                        // in one long scrolling list; tabbing into results
                        // triggered focus-into-view auto-scroll back to the
                        // top, looking to the user like the letter filter
                        // had been cleared. Filtering by selectedFilterLetter
                        // here means tab order stays within the chosen
                        // letter's items.
                        Object.keys(filters[selectedFilter])
                          .filter((key) => !selectedFilterLetter || key === selectedFilterLetter)
                          .map(function(key) {
                            const isInAlpha = alpha.indexOf(key.toUpperCase().charCodeAt(0)) !== -1;
                            return filters[selectedFilter][key].map(function(name, index) {
                              const isSelected = this.isSelected(name);
                              return (
                                <li className={isSelected ? "selected" : ""} id={isInAlpha && index === 0 ? `filterResultLetter-${key}` : ""} key={index}>
                                  <button type="button" onClick={() => this.onSearch(name)} aria-pressed={!!isSelected}>{name}</button>
                                </li>
                              )
                            }.bind(this))
                          }.bind(this))
                      }
                      {
                        Object.keys(initialFilters[selectedFilter])
                          .filter((key) => !selectedFilterLetter || key === selectedFilterLetter)
                          .map(function(key) {
                            return initialFilters[selectedFilter][key].filter(function(el) {
                              return !filters[selectedFilter][key] || filters[selectedFilter][key].indexOf(el) === -1;
                            }).map(function(name, index) {
                              return (
                                <li className="disabled" key={index}>
                                  <button type="button" onClick={() => this.onSearch(name)}>{name}</button>
                                  {this.isSelected(name) && <button type="button" aria-label={`Remove ${name} filter`}><SVG.Close className="close-icon" onClick={() => this.onSearch()} /></button>}
                                </li>
                              )
                            }.bind(this))
                          }.bind(this))
                      }
                      </ul>
                    }
                  </div>
                }

              </React.Fragment>
              }
            </div>
          }
        </div>
      </div>
    )
  }
};

export default compose(withRouter, withStore)(SearchFilters);
